import { Link, useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { useContactHandler } from "../../../lists/components/useContactHandler";
import {
  downloadCampaignsContact,
  downloadCampaignsContactStep,
  findCampaignStats,
  getProgrammationContacts,
  getProgrammationUniqueContacts,
} from "../../../../../api/StatsAPI";
import TableFilterExport from "../../../../../components/TableComponents/TableFilterExport";
import {
  ActionIcon,
  Anchor,
  Button,
  Divider,
  Flex,
  Group,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import MainTable from "../../../../../layouts/Table/MainTable";
import { useListStore } from "../../../../../stores/ListStore";
import { booleanColumn } from "../../../../../layouts/Table/columns/booleanColumn";
import { IconArrowLeft, IconExternalLink, IconPlus } from "@tabler/icons-react";
import {
  parseProspectDatafields,
  status_list,
} from "../../../leadsTracking/utils";
import dateColumn from "../../../../../layouts/Table/columns/dateColumn";
import Answers from "../../../leadsTracking/prospect/answers/Answers";
import numberColumn from "../../../../../layouts/Table/columns/numberColumn";
import ClickerLinks from "./ClickerLinks";
import { parseDate } from "../../../../../functions/dates";
import TableFilterBlacklistContacts from "./TableFilterBlacklistContacts";
import ZapierSettings from "../../../leadsTracking/titleSection/zapier/ZapierSettings";
import Signage from "../../../../../layouts/Header/Signage";
import {
  getNodeDetails,
  getTypeIcon,
} from "../../../workflows/CreateCampaign/flow/flowSettings";
import { useAccountsStore } from "../../../../../stores/ConfigStore";
import StatsContactsCopyToList from "./actions/StatsContactsCopyToList";

function filterPrmById(id) {
  return "/leads-tracking?contact_id=" + id;
}

const canVisualize = ["contacted", "answerer", "clicker", "opener"];
function parseStatus(status, type) {
  if (status === "opener") {
    if (type === "linkedin_invitation") {
      return "last_linkedin_invitation_accepted";
    }
    if (type === "sms") {
      return "last_sms_got";
    }
    return "last_email_open";
  }
  if (status === "clicker") {
    return "last_link_click";
  }
  if (status === "answerer") {
    if (type === "linkedin_message") {
      return "last_linkedin_message_answered";
    }
    return "last_reply";
  }
  return null;
}

function handlePrmRedirectLink({ contact_type, campaignId, step_type }) {
  const params = new URLSearchParams(location.search);
  const createdOn = params.get("createdOn");
  if (contact_type === "contacted") {
    return `/leads-tracking?programmation_id=${campaignId}&created_on=${createdOn}`;
  }
  return `/leads-tracking?programmation_id=${campaignId}&created_on=${createdOn}&contact_type=${parseStatus(
    contact_type,
    step_type,
  )}`;
}

function getName({ campaign, type, stepType }) {
  const params = new URLSearchParams(location.search);
  const workflow_name = params.get("workflow_name");

  const lang = useLanguage.getState().language;

  let contacts_type =
    lang.statistics.contacts_type?.[type] ||
    lang.statistics.contacts_type?.[stepType];

  return `${contacts_type ? `${contacts_type} - ` : ""}${workflow_name}${
    campaign ? " #" + campaign : ""
  }`;
}

const CampaignContacts = () => {
  const navigate = useNavigate();
  const { campaign, workflow, stepId, type, stepType } = useParams();
  const lang = useLanguage((s) => s.language);
  const apiRef = useGridApiRef();

  const [progStats, setProgStats] = useState(null);

  const tabs = useMemo(() => {
    let uniqueList = {
      contacted: lang.statistics.contacts_type.contacted,
      // opener: lang.statistics.contacts_type.opener, // (CNIL)
      clicker: lang.statistics.contacts_type.clicker,
      answerer: lang.statistics.contacts_type.answerer,
      bounced: lang.statistics.contacts_type.bounced,
      blacklisted: lang.statistics.contacts_type.blacklisted,
      unsubscriber: lang.statistics.contacts_type.unsubscriber,
      excluded_previous_programmation:
        lang.statistics.contacts_type.excluded_previous_programmation,
    };
    let list = {
      contacted: lang.statistics.contacts_type.contacted,
      sent_today: lang.statistics.contacts_type.sent_today,
      // opener: lang.statistics.contacts_type.opener, // (CNIL)
      clicker: lang.statistics.contacts_type.clicker,
      answerer: lang.statistics.contacts_type.answerer,
      unsubscriber: lang.statistics.contacts_type.unsubscriber,
      bounced: lang.statistics.contacts_type.bounced,
      to_contact: lang.statistics.contacts_type.to_contact,
      blacklisted: lang.statistics.contacts_type.blacklisted,
      excluded_previous_programmation:
        lang.statistics.contacts_type.excluded_previous_programmation,
      excluded_workflow: lang.statistics.contacts_type.excluded_workflow,
      to_contact_failed: lang.statistics.contacts_type.to_contact_failed,
    };

    const r = Object.keys(stepId ? list : uniqueList).map((key) => ({
      value: key,
      label: `${list[key]} ${
        progStats?.[key] === undefined || stepId
          ? ""
          : `(${progStats?.[key] || "0"})`
      } `,
    }));

    return r;
  }, [lang, progStats, stepId]);

  const lists = useListStore((s) => s.lists);

  const startingColumns = useMemo(() => {
    return [
      {
        field: "status",
        id: "status",
        headerName: lang.statistics.columns.contacted.columns.status,
        sortable: false,
        width: 120,
        filterable: false,
        renderCell: ({ value }) => {
          if (!value) {
            return "-";
          }
          if (value === "opener" && stepType === "linkedin_invitation") {
            return lang.workflows.nodes.events.linkedin_invitation_accepted;
          }
          if (value === "opener") {
            return "";
          }
          return value
            ? status_list().find((x) => x.value === value).label
            : lang.workflows.nodes.sent;
        },
      },
      {
        field: "date_contacted",
        headerName: lang.statistics.columns.contacted.columns.date_contacted,
        width: 150,
        // sortable: false,
        type: "date",
        id: "date_contacted",
        valueGetter: (params) => {
          return params?.value ? new Date(params.value) : null;
        },
        renderCell: (cell) => {
          if (cell.value)
            return (
              <Tooltip
                label={new Date(cell.row["date_contacted"]).toLocaleString()}
                withArrow
                openDelay={500}
              >
                <Text fz={13} truncate="end">
                  {parseDate(cell.value)}
                </Text>
              </Tooltip>
            );
          return lang.workflows.nodes.waiting;
        },
        filterable: !!stepId,
      },
      dateColumn({
        field: "status_changed_date",
        filterable: true,
        sortable: true,
        headerName:
          lang.statistics.columns.contacted.columns.status_changed_date,
      }),
      booleanColumn({
        field: "is_bounced",
        headerName: lang.statistics.contacts_type.bounced,
        falseLabel: lang.linkedin_accounts.columns.not_sn,
        trueLabel: lang.linkedin_accounts.columns.is_sn,
        inverse: true,
        filterable: false,
        sortable: false,
      }),
      {
        field: "email_prefered_integration_id",
        id: "email_prefered_integration_id",
        headerName: "Email " + lang.statistics.columns.sender.header_name,
        sortable: false,
        width: 150,
        filterable: false,
        renderCell: ({ value }) => {
          const account = useAccountsStore
            .getState()
            .email_accounts.find((x) => x.id === value);

          if (!value || !account) {
            return "-";
          }
          return (
            <Tooltip
              label={`${lang.statistics.columns.sender.view} : ${
                account?.name
              } ${account?.id ? `#${account.id}` : ""}`}
              disabled={!account?.id}
            >
              <Anchor
                component={account?.id ? Link : undefined}
                size="sm"
                c={"black"}
                truncate="end"
                to={`/senders/email/${account.id}`}
                fw={600}
                flex={"1 1 auto"}
                underline={"hover"}
                style={{
                  cursor: "pointer",
                }}
              >
                {account?.name || "-"} {account?.id ? `#${account.id}` : ""}
              </Anchor>
            </Tooltip>
          );
        },
      },
    ].filter((x) => (stepId ? true : x.field !== "date_contacted"));
  }, [lang, stepId, stepType]);

  const additionalColumns = useMemo(() => {
    const result = [
      numberColumn(),
      {
        field: "contact_list_id",
        id: "contact_list_id",
        headerName: lang.prm.details.campaigns_table.contact_list,
        filterable: false,
        sortable: false,
        width: 150,
        renderCell: ({ row, value }) => {
          return (
            <Anchor
              component={Link}
              to={`/contact-lists/${value}`}
              target="_blank"
            >
              {lists.find((x) => x.id === value)?.name || "Liste"} #{value}
            </Anchor>
          );
        },
      },
      {
        field: "contact_list_contacts_id",
        headerName: lang.prm.details.campaigns_table.id_in_lilst,
        id: "contact_list_contacts_id",
        filterable: false,
        sortable: false,
        width: 100,
        renderCell: ({ row, value }) => {
          let f = {
            mode: "and",
            values: [
              {
                field_name: "id",
                type: "equals",
                value: value,
              },
            ],
          };
          return (
            <Anchor
              component={Link}
              to={`/contact-lists/${
                row.contact_list_id
              }/?filter=${JSON.stringify(f)}`}
              target="_blank"
            >
              {value ? `#${value}` : "-"}
            </Anchor>
          );
        },
      },
      {
        field: "prm_contacts_id",
        headerName: lang.statistics.columns.contacted.columns.prm_contacts_id,
        sortable: false,
        id: "prm_contacts_id",
        filterable: false,
        width: 120,
        renderCell: ({ value }) =>
          value ? (
            <Anchor href={filterPrmById(value)} target="_blank">
              #{value}
            </Anchor>
          ) : (
            "-"
          ),
      },
      {
        id: "date_added_to_contact_list",
        field: "date_added_to_contact_list",
        headerName:
          lang.table.toolbar.filter.filters_list_label.server
            .date_added_to_contact_list,
        type: "date",
        filterable: true,
        sortable: false,
        width: 75,
        renderCell: ({ row, value }) => {
          return "-";
        },
      },
    ];

    return result;
  }, [lang, lists]);

  const [answerContainer, setAnswerContainer] = useState({
    nextPageUrl: null,
    items: [],
  });

  const {
    loading,
    columns,
    rows,
    rowCount,
    serverInfo,
    handleLoading,
    onFilterChange,
    onPaginationChange,
    onSortChange,
  } = useContactHandler({
    apiRef,
    listId: parseInt(campaign),
    getFirstPage: (data) =>
      stepId
        ? getProgrammationContacts({
            programmation_id: parseInt(campaign),
            workflow_id: parseInt(workflow),
            step_id: parseInt(stepId),
            contact_type: type,
            ...data,
          })
        : getProgrammationUniqueContacts({
            programmation_id: parseInt(campaign),
            workflow_id: parseInt(workflow),
            contact_type: type,
            ...data,
          }),
    searchFunction: ({ query, filter, ...data }) =>
      stepId
        ? getProgrammationContacts({
            programmation_id: parseInt(campaign),
            workflow_id: parseInt(workflow),
            step_id: parseInt(stepId),
            contact_type: type,
            filter: {
              mode: "and",
              values: [
                ...(filter?.values || []),
                {
                  field_name: "any_datafield",
                  type: "contains",
                  value: query,
                },
              ],
            },
            ...data,
          })
        : getProgrammationUniqueContacts({
            programmation_id: parseInt(campaign),
            workflow_id: parseInt(workflow),
            contact_type: type,
            filter: {
              mode: "and",
              values: [
                ...(filter?.values || []),
                {
                  field_name: "any_datafield",
                  type: "contains",
                  value: query,
                },
              ],
            },
            ...data,
          }),
    disableEdit: true,
    startingColumns,
    additionalColumns,
    additionalFields: { date_added_to_contact_list: null },
    disableQueryTransform: true,
    onFinish: ({ rows, serverInfo }) =>
      setAnswerContainer({
        nextPageUrl: serverInfo.next_page,
        items: rows.map((x) => ({
          ...x,
          ...parseProspectDatafields(x),
          id: x.prm_contacts_id,
        })),
      }),

    onPageFinished: ({ rows, serverInfo }) => {
      setAnswerContainer({
        nextPageUrl: serverInfo.next_page,
        items: rows.map((x) => ({
          ...x,
          ...parseProspectDatafields(x),
          id: x.prm_contacts_id,
        })),
      });
    },
  });

  const actionsCell = useCallback(
    ({ row }) => {
      return (
        <Answers
          container={answerContainer}
          setContainer={setAnswerContainer}
          index={answerContainer.items.findIndex(
            (x) => x.prm_contacts_id === row.prm_contacts_id,
          )}
          inSheet
          inTable
          disabled={row?.status !== "answerer"}
        />
      );
    },
    [answerContainer],
  );

  const toolbarActions = useCallback(
    ({ selectedIds, filter }) => (
      <>
        <TableFilterExport
          id={parseInt(campaign)}
          selectedIds={selectedIds}
          filter={filter}
          handleExport={({ ids, excluded_ids, filter, file_type, name }) => {
            let main = stepId
              ? downloadCampaignsContactStep
              : downloadCampaignsContact;

            return main({
              contact_type: type,
              file_type,
              programmation_id: parseInt(campaign),
              step_id: parseInt(stepId),
              workflow_id: parseInt(workflow),
              name,
              contacts_selection: {
                contact_ids: ids,
                excluded_contact_ids: excluded_ids,
                filter,
              },
            });
          }}
          name={getName({ campaign, stepType, type })}
          filterResultNumber={rowCount}
        />

        {stepId && (
          <StatsContactsCopyToList
            tableFilter={filter}
            tableSelection={selectedIds}
            handleFinish={() => {}}
          />
        )}

        {stepId && (
          <TableFilterBlacklistContacts
            total={rowCount}
            selectedIds={selectedIds}
            filter={filter}
            filterResultNumber={rowCount}
          />
        )}
      </>
    ),
    [campaign, stepType, type, rowCount, stepId, workflow],
  );

  useEffect(() => {
    if (apiRef.current?.state) {
      handleLoading(true);
    }
  }, [type, apiRef]);

  useEffect(() => {
    findCampaignStats(campaign).then((response) => {
      let data = response.data.programmation;
      setProgStats({
        ...data,
        contacted: data.contacted,
        sent_today: data.sent_today,
        opener: data.contacts_opened,
        clicker: data.contacts_clicked,
        answerer: data.contacts_answered,
        unsubscriber: data.unsubscribers,
        bounced: data.bounced,
        to_contact: data.to_contact,
        blacklisted: data.blacklisted,
        excluded_previous_programmation: data.excluded_previous_programmation,
        excluded_workflow: data.contacts_answered,
        to_contact_failed: data.to_contact_failed,
      });
    });
  }, [campaign]);

  if (!columns) {
    return;
  }

  return (
    <Stack gap={0} px={64} mt={24} pb={16} className="campaigns-page">
      <Group gap={"lg"} mb={16}>
        <ActionIcon size={"xl"} component={Link} to={"/statistics/campaigns"}>
          <IconArrowLeft />
        </ActionIcon>
        <Paper component={Group} py={"sm"} px={"xl"} gap={"xl"}>
          <Text fw={700} fz={24}>
            {tabs.find((x) => x.value === type).label}
          </Text>

          <Divider orientation="vertical" />

          <Text size="lg" fw={600}>
            {lang.campaigns.campaign} #{campaign}
          </Text>

          {stepId && (
            <>
              <Divider orientation="vertical" />

              {getTypeIcon(stepType, true, 32, {
                size: "md",
                opacity: 0.75,
                variant: "subtle",
                color: getNodeDetails(stepType)?.color || "secondary",
              })}
              <Text size="lg" fw={600} ml={-16}>
                {lang.campaigns.step} #{stepId}
              </Text>
            </>
          )}
        </Paper>

        {!stepId && (
          <Tooltip label={lang.statistics.columns.contacted.view_in_prm}>
            <ActionIcon
              size={"lg"}
              component={Link}
              to={handlePrmRedirectLink({
                contact_type: type,
                campaignId: campaign,
                step_type: stepType,
              })}
              disabled={!canVisualize.includes(type)}
              variant="light"
            >
              <IconExternalLink />
            </ActionIcon>
          </Tooltip>
        )}

        <Group gap={"lg"} ml={"auto"}>
          <ZapierSettings type="statistics" />
          <Signage />
          <Button
            leftSection={<IconPlus size={18} />}
            radius={"md"}
            color="primary"
            component={Link}
            to={"/campaign/create"}
            size="md"
          >
            {lang.campaigns.create_campaign}
          </Button>
        </Group>
      </Group>
      <Group flex={"1 1 auto"} mb={8} pos={"relative"} style={{ zIndex: 8 }}>
        <SegmentedControl
          value={type}
          onChange={(v) => {
            const params = new URLSearchParams(location.search);
            const createdOn = params.get("createdOn");

            if (stepId) {
              navigate(
                `/statistics/campaigns/${campaign}/workflow/${workflow}/step/${stepId}/contacts/${v}/${stepType}?createdOn=${createdOn}`,
              );
            } else {
              navigate(
                `/statistics/campaigns/${campaign}/workflow/${workflow}/contacts/${v}?createdOn=${createdOn}`,
              );
            }
          }}
          size="md"
          w={"100%"}
          data={tabs}
          disabled={loading}
          // color="secondary"
          styles={{
            root: {
              overflow: "auto",
            },
          }}
        />
      </Group>
      <Flex flex={"1 1 auto"} h={"calc(100vh - 225px)"}>
        <Flex flex={"1 1 auto"}>
          <MainTable
            isClient={false}
            apiRef={apiRef}
            columns={columns}
            rows={rows}
            rowCount={rowCount}
            loading={loading}
            setLoading={() => {
              handleLoading();
            }}
            id={"list-table"}
            pageName={"stats_fetch_contacts_order"}
            onPaginationChange={onPaginationChange}
            onFilterChange={onFilterChange}
            onSortChange={onSortChange}
            pageOrderName="stats_fetch_contacts_order"
            toolbarActions={toolbarActions}
            actionsCell={actionsCell}
            actionsCellWidth={50}
            withBorderTop
            whiteButtons
            defaultSort={[{ field: "status_changed_date", sort: "desc" }]}
            additionalFilter={[
              {
                field_name: "date_added_to_contact_list",
                id: "date_added_to_contact_list",
                name: lang.table.toolbar.filter.filters_list_label.server
                  .date_added_to_contact_list,
                label:
                  lang.table.toolbar.filter.filters_list_label.server
                    .date_added_to_contact_list,
                option_name:
                  lang.table.toolbar.filter.filters_list_label.server
                    .date_added_to_contact_list,
                type: "date",
                filterable: true,
              },
            ]}
          />

          {type === "clicker" && stepId && <ClickerLinks />}
        </Flex>
      </Flex>
    </Stack>
  );
};

export default CampaignContacts;
